import { useState } from 'react'
import {
  Eye, Heart, ImageIcon, Link2, MessageCircle, Nfc, PieChart, Play, RefreshCw, Sparkles, Users,
} from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link } from '../router.js'
import { ToastProvider, useToast, adminMutate, usePermissions } from '../admin/primitives.js'
import { registerPageLayout } from '../layout/registry.js'
import { LayoutEditControls, LayoutGrid, useLayoutState } from '../layout/LayoutGrid.js'
import { InstagramDataProvider, useInstagram } from './InstagramData.js'
import { Trail } from '../navigation/Trail.js'
import { SITE_FAMILIES } from '../navigation/site-navigation.js'
import { ChevronRight } from 'lucide-react'
import { PERIODES, fraicheurFr } from './lib.js'
import {
  IgAudienceWidget, IgCommentsWidget, IgDemographicsWidget, IgInteractionsWidget,
  IgProfileWidget, IgReachViewsWidget, IgReelsWidget, IgStoriesWidget, IgTopContentWidget,
} from './InstagramWidgets.js'
import { IgAcquisitionWidget } from './AcquisitionWidgets.js'
import { useMenuLien } from '../layout/menu-tuiles.js'

/**
 * /communication/instagram — L'ANALYTICS INSTAGRAM DU CLUB (V1.1).
 *
 * Page 100 % personnalisable (Page Layout Engine existant, page_key
 * `communication-instagram`) : drag / resize / masquage / réglages par tuile
 * / Save Bar. AUCUN drawer. Toutes les données viennent de PostgreSQL —
 * Meta n'est jamais appelé au rendu.
 */

registerPageLayout({
  pageKey: 'communication-instagram',
  widgets: [
    { id: 'ig-audience', Icon: Users, label: 'Audience', description: 'Abonnés, croissance, courbe et solde des abonnements', minW: 5, minH: 4, defaultW: 8, defaultH: 5, Component: IgAudienceWidget },
    { id: 'ig-reach-views', Icon: Eye, label: 'Vues & Portée', description: 'Vues (affichages) et comptes atteints (uniques)', minW: 3, minH: 3, defaultW: 4, defaultH: 5, Component: IgReachViewsWidget },
    { id: 'ig-interactions', Icon: Heart, label: 'Interactions', description: 'Likes, commentaires, partages, sauvegardes, réponses', minW: 3, minH: 3, defaultW: 4, defaultH: 4, Component: IgInteractionsWidget },
    { id: 'ig-profile', Icon: Link2, label: 'Activité du profil', description: 'Visites du profil et clics sur les liens', minW: 3, minH: 2, defaultW: 4, defaultH: 3, Component: IgProfileWidget },
    { id: 'ig-top-content', Icon: Sparkles, label: 'Top contenus', description: 'Les contenus qui performent, classés par vraie métrique', minW: 3, minH: 3, defaultW: 4, defaultH: 4, Component: IgTopContentWidget },
    { id: 'ig-reels', Icon: Play, label: 'Reels', description: 'Performance des Reels, temps de visionnage inclus', minW: 3, minH: 3, defaultW: 4, defaultH: 4, Component: IgReelsWidget },
    { id: 'ig-stories', Icon: ImageIcon, label: 'Stories', description: 'Stories capturées pendant leurs 24 h de vie', minW: 3, minH: 3, defaultW: 4, defaultH: 4, Component: IgStoriesWidget },
    { id: 'ig-demographics', Icon: PieChart, label: 'Démographie', description: 'Agrégats d’audience Meta quand disponibles', minW: 3, minH: 3, defaultW: 4, defaultH: 4, Component: IgDemographicsWidget },
    { id: 'ig-comments', Icon: MessageCircle, label: 'Commentaires récents', description: 'Les derniers commentaires, avec leurs auteurs réels', minW: 3, minH: 3, defaultW: 4, defaultH: 4, Component: IgCommentsWidget },
    { id: 'ig-acquisition', Icon: Nfc, label: 'Trafic des supports du club', description: 'Clics QR/NFC/liens du tracker, mis en regard de l’activité Meta', minW: 4, minH: 4, defaultW: 8, defaultH: 4, Component: IgAcquisitionWidget },
  ],
  defaultLayout: [
    { id: 'ig-audience', x: 0, y: 0, w: 8, h: 5 },
    { id: 'ig-reach-views', x: 8, y: 0, w: 4, h: 5 },
    { id: 'ig-interactions', x: 0, y: 5, w: 4, h: 4 },
    { id: 'ig-top-content', x: 4, y: 5, w: 4, h: 4 },
    { id: 'ig-reels', x: 8, y: 5, w: 4, h: 4 },
    { id: 'ig-profile', x: 0, y: 9, w: 4, h: 3 },
    { id: 'ig-stories', x: 4, y: 9, w: 4, h: 4 },
    { id: 'ig-demographics', x: 8, y: 9, w: 4, h: 4 },
    // Optionnel par défaut (§28) : disponible dans la bibliothèque, masqué
    // tant qu'on ne l'active pas — la home ne se surcharge pas toute seule.
    { id: 'ig-comments', x: 0, y: 13, w: 4, h: 4, visibility: 'HIDDEN' },
    // ACQUISITION CLUB (V1) : visible par défaut — c'est la mise en regard
    // tracker/Meta demandée par le contrat, en bas de page.
    { id: 'ig-acquisition', x: 0, y: 13, w: 8, h: 4 },
  ],
})

function BoutonSyncIg() {
  const toast = useToast()
  const { reload } = useInstagram()
  const [busy, setBusy] = useState(false)
  const [bilan, setBilan] = useState<string | null>(null)
  const permissions = usePermissions()
  if (!permissions.permissions.includes('communication.settings.manage')) return null
  return (
    <>
      <button type="button" className="lc-btn" disabled={busy} onClick={async () => {
        setBusy(true); setBilan(null)
        const r = await adminMutate<{ error?: string; ok?: boolean; detail?: string; message?: string }>(
          'POST', '/api/v1/communication/integrations/instagram/sync')
        setBusy(false)
        if (r.status === 200 && r.data.ok !== false) {
          setBilan(r.data.detail ?? 'Synchronisation terminée.')
          toast('ok', 'Synchronisation terminée.')
          reload()
        } else toast('error', r.data.detail ?? r.data.message ?? 'Synchronisation impossible.')
      }}>
        <RefreshCw size={14} aria-hidden="true" className={busy ? 'cm-spin' : ''} />
        {busy ? 'Synchronisation…' : 'Synchroniser maintenant'}
      </button>
      {bilan ? <span className="lc-hint ig-syncbilan">{bilan}</span> : null}
    </>
  )
}

function SelecteurPeriodeIg() {
  const { periode, setPeriode } = useInstagram()
  return (
    <div className="lc-seg cm-periods" role="group" aria-label="Période">
      {PERIODES.map((p) => (
        <button key={p.value} type="button"
          className={`lc-seg__btn ${periode === p.value ? 'lc-seg__btn--on' : ''}`}
          aria-pressed={periode === p.value}
          onClick={() => setPeriode(p.value)}>{p.label}</button>
      ))}
    </div>
  )
}

function PageContent() {
  const layout = useLayoutState('communication-instagram')
  const { data } = useInstagram()
  const menuLien = useMenuLien()
  return (
    <>
      <header className="cm-head">
        <div>
          <h1 className="lc-title">Instagram</h1>
          <p className="lc-subtitle">
            {data?.account.username ? <>@{data.account.username} · </> : null}
            {data?.connected
              ? <span className="lc-pill lc-pill--ok ig-badge"><span className="lc-dot lc-dot--ok lc-dot--live" />Insights connectés</span>
              : <span className="lc-pill lc-pill--idle ig-badge">Lecture publique limitée</span>}
            {data?.token.status === 'RENOUVELLEMENT_A_VENIR' ? <span className="lc-pill lc-pill--warning ig-badge">Renouvellement à venir</span> : null}
            {data?.token.status === 'A_RENOUVELER' || data?.state === 'RECONNECT'
              ? <span className="lc-pill lc-pill--warning ig-badge">Connexion à renouveler</span> : null}
            {data?.lastSyncAt ? <span className="lc-hint"> mis à jour {fraicheurFr(data.lastSyncAt)}</span> : null}
          </p>
          {data && !data.connected ? (
            <p className="lc-hint">
              Reach, vues, interactions et contenus détaillés attendent la connexion
              propriétaire — <Link href="/communication/integrations/instagram">connecter le compte du club</Link>.
            </p>
          ) : null}
        </div>
        <div className="cm-head__actions">
          <SelecteurPeriodeIg />
          <BoutonSyncIg />
          <LayoutEditControls state={layout} />
        </div>
      </header>
      {layout.loaded ? <LayoutGrid pageKey="communication-instagram" state={layout} /> : null}
      {/* V2.1 §51-53 : le hub présente ses FONCTIONS — tuiles du registre,
          aucun cas spécial : c'est la même grammaire que les hubs de famille. */}
      <div className="fh-grid igm-fonctions">
        {(SITE_FAMILIES.find((f) => f.id === 'communication')!.children
          .find((c) => c.id === 'instagram')!.children ?? [])
          .filter((x) => x.route && x.route !== '/communication/instagram')
          .map((x) => (
            <Link key={x.id} href={x.route!} className="lc-card fh-tile lc-in" {...menuLien(x.route, x.label)}>
              <span className="lc-card__edge" aria-hidden="true" />
              <span className="lc-card__icon" aria-hidden="true"><x.icon /></span>
              <div className="fh-tile__texte">
                <h2>{x.label}</h2>
                <p className="lc-hint">{x.description}</p>
              </div>
              <div className="fh-tile__pied">
                <span className="fh-tile__cta"><ChevronRight size={16} aria-hidden="true" /></span>
              </div>
            </Link>
          ))}
      </div>
    </>
  )
}

export function InstagramAnalyticsPage() {
  const meta = useShellMeta()
  return (
    <Shell active="communication" meta={meta}>
      <main className="lc-page lc-stagger cm-page">
        <Trail />
        <ToastProvider>
          <InstagramDataProvider>
            <PageContent />
          </InstagramDataProvider>
        </ToastProvider>
      </main>
    </Shell>
  )
}
